import type { ErrorNote, Job } from "../types";
import { getJobAgeColor } from "./jobAgeColor";
import { GEO_VALIDATOR_SOURCE } from "./validateJobGeometry";

/** One group of a job list, with its jobs. */
export interface JobGroup {
	/** Identifies the group (its header text may not be unique). */
	key: string;
	/** The group's header text. */
	label: string;
	jobs: Job[];
}

/** What a job list groups its jobs by; "none" is one flat, headerless group. */
export type JobListGroupBy = "none" | "company" | "shift" | "error" | "status" | "grower" | "branch";

/** How a job list orders its jobs (and, through them, its groups). */
export type JobListSort = "oldest" | "newest" | "fieldName" | "errorCount";

/** Narrows a job list. Empty selections and a null age don't constrain it. */
export interface JobListFilter {
	/** Case-insensitive match on field, farm, grower, company, status and the listed errors. */
	search: string;
	/** Error types (see `errorTypeOf`); a job passes with any one of them. */
	errorTypes: readonly string[];
	companies: readonly string[];
	statuses: readonly string[];
	/** Only jobs at least this many business days old — the age the job cards show. */
	minAgeDays: number | null;
}

export const EMPTY_JOB_LIST_FILTER: JobListFilter = {
	search: "",
	errorTypes: [],
	companies: [],
	statuses: [],
	minAgeDays: null,
};

/** How many of the filter's constraints are on. */
export function countActiveJobListFilters(filter: JobListFilter): number {
	return (
		(filter.search.trim() ? 1 : 0) +
		(filter.errorTypes.length > 0 ? 1 : 0) +
		(filter.companies.length > 0 ? 1 : 0) +
		(filter.statuses.length > 0 ? 1 : 0) +
		(filter.minAgeDays !== null ? 1 : 0)
	);
}

type JobDate = Date | string | null | undefined;

/** Everything `organizeJobList` needs besides the jobs. */
export interface JobListOptions {
	groupBy: JobListGroupBy;
	sort: JobListSort;
	filter: JobListFilter;
	/** The job date its age counts from. */
	dateOf: (job: Job) => JobDate;
	/** The errors the list shows for a job. */
	errorsOf: (job: Job) => readonly ErrorNote[];
	/** Display names for shift and branch groups; a missing name falls back to the raw id. */
	shiftNames?: ReadonlyMap<string, string>;
	branchNames?: ReadonlyMap<string, string>;
	now?: Date;
}

/** A job's errors from one source, or all of them when `source` is null. */
export function softwareErrorsFrom(job: Job, source: string | null): ErrorNote[] {
	const errors = job.softwareErrors ?? [];
	return source === null ? errors : errors.filter((e) => e.source === source);
}

/** Whether pre-sampling-processor filed any Geo-Spatial Validator errors on the job. */
export function hasGeoValidatorErrors(job: Job): boolean {
	return softwareErrorsFrom(job, GEO_VALIDATOR_SOURCE).length > 0;
}

/**
 * The type of an error note: the note with its ids and counts collapsed to "#", so "Core 8.1 is too short" and
 * "Core 12.3 is too short" are one type. A run of ids ("samples: 1, 2, 3") collapses to a single "#".
 */
export function errorTypeOf(note: string): string {
	return note
		.replace(/\d+(?:[.,;-]\d+)*/g, "#")
		.replace(/#(?:\s*,\s*#)+/g, "#")
		.trim();
}

/** A job's distinct error types, in the order they first appear. */
export const jobListErrorTypesOf = (errors: readonly ErrorNote[]): string[] => [
	...new Set(errors.map((e) => errorTypeOf(e.note))),
];

/** Epoch ms of a job date, or null when it's missing or unparseable (dates can arrive as ISO strings). */
const timeOf = (date: JobDate): number | null => {
	if (!date) return null;
	const time = (date instanceof Date ? date : new Date(date)).getTime();
	return Number.isNaN(time) ? null : time;
};

/** Undated jobs sink to the bottom whichever way the age runs. */
const byTime = (a: number | null, b: number | null, direction: 1 | -1): number => {
	if (a === null) return b === null ? 0 : 1;
	if (b === null) return -1;
	return (a - b) * direction;
};

const matchesFilter = (job: Job, errors: readonly ErrorNote[], filter: JobListFilter, options: JobListOptions) => {
	const search = filter.search.trim().toLowerCase();
	if (search) {
		const haystack = [
			job.fieldName,
			job.farmName,
			job.growerName,
			job.companyName,
			job.status,
			...errors.map((e) => e.note),
		]
			.filter(Boolean)
			.join("\n")
			.toLowerCase();
		if (!haystack.includes(search)) return false;
	}
	if (filter.errorTypes.length > 0 && !jobListErrorTypesOf(errors).some((t) => filter.errorTypes.includes(t)))
		return false;
	if (filter.companies.length > 0 && !filter.companies.includes(jobListCompanyOf(job))) return false;
	if (filter.statuses.length > 0 && !filter.statuses.includes(jobListStatusOf(job))) return false;
	if (filter.minAgeDays !== null) {
		const date = options.dateOf(job);
		if (!date || getJobAgeColor(date, options.now).businessDaysOld < filter.minAgeDays) return false;
	}
	return true;
};

/** The company a job list files a job under, with the list's fallback for none. */
export const jobListCompanyOf = (job: Job) => job.companyName || "Unknown Company";
/** The status a job list files a job under, with the list's fallback for none. */
export const jobListStatusOf = (job: Job) => job.status || "No Status";
/** The grower a job list files a job under, with the list's fallback for none. */
export const jobListGrowerOf = (job: Job) => job.growerName || "Unknown Grower";

/** The groups a job belongs to. Only error grouping can put a job in more than one: one group per error type. */
const groupsOf = (
	job: Job,
	errors: readonly ErrorNote[],
	options: JobListOptions,
): { key: string; label: string }[] => {
	switch (options.groupBy) {
		case "none":
			return [{ key: "__all__", label: "" }];
		case "company":
			return [{ key: jobListCompanyOf(job), label: jobListCompanyOf(job) }];
		case "status":
			return [{ key: jobListStatusOf(job), label: jobListStatusOf(job) }];
		case "grower":
			return [{ key: jobListGrowerOf(job), label: jobListGrowerOf(job) }];
		case "shift":
			if (!job.shiftId) return [{ key: "__no_shift__", label: "No Shift" }];
			return [{ key: job.shiftId, label: options.shiftNames?.get(job.shiftId) || job.shiftId }];
		case "branch": {
			const branchId = job.branchIds?.[0];
			if (!branchId) return [{ key: "__no_branch__", label: "No Branch" }];
			return [{ key: branchId, label: options.branchNames?.get(branchId) || branchId }];
		}
		case "error": {
			const types = jobListErrorTypesOf(errors);
			if (types.length === 0) return [{ key: "__no_errors__", label: "No Errors" }];
			return types.map((type) => ({ key: type, label: type }));
		}
	}
};

/**
 * Filters, sorts and groups a job list:
 *   - Jobs sort by `sort`; ties (and undated jobs under an age sort) fall back to field name.
 *   - Under an age sort, groups sort by their top job, so the oldest (or newest) job overall is at the top. Under the
 *     other sorts they sort by label. Ties sort by label.
 *   - Grouping by error puts a job with several error types in each of their groups.
 */
export function organizeJobList(jobs: readonly Job[], options: JobListOptions): JobGroup[] {
	const { sort, filter } = options;
	const direction = sort === "newest" ? -1 : 1;
	const entries = jobs
		.map((job) => {
			const errors = options.errorsOf(job);
			return { job, errors, time: timeOf(options.dateOf(job)) };
		})
		.filter(({ job, errors }) => matchesFilter(job, errors, filter, options));

	type Entry = (typeof entries)[number];
	const byName = (a: Entry, b: Entry) => (a.job.fieldName || "").localeCompare(b.job.fieldName || "");
	const compare = (a: Entry, b: Entry): number => {
		switch (sort) {
			case "oldest":
			case "newest":
				return byTime(a.time, b.time, direction) || byName(a, b);
			case "fieldName":
				return byName(a, b);
			case "errorCount":
				return b.errors.length - a.errors.length || byName(a, b);
		}
	};
	entries.sort(compare);

	// Entries are already sorted, so each group's jobs arrive in order.
	const groups = new Map<string, { label: string; entries: Entry[] }>();
	for (const entry of entries) {
		for (const { key, label } of groupsOf(entry.job, entry.errors, options)) {
			const group = groups.get(key) ?? { label, entries: [] };
			group.entries.push(entry);
			groups.set(key, group);
		}
	}

	const byAge = sort === "oldest" || sort === "newest";
	return Array.from(groups.entries())
		.map(([key, group]) => ({ key, ...group }))
		.sort(
			(a, b) => (byAge ? byTime(a.entries[0].time, b.entries[0].time, direction) : 0) || a.label.localeCompare(b.label),
		)
		.map(({ key, label, entries }) => ({ key, label, jobs: entries.map((e) => e.job) }));
}

/** One value a job list can be filtered to, with how many jobs have it. */
export interface JobListFacet {
	value: string;
	count: number;
}

/** The values each of a job list's filters can pick from, most common first. */
export function jobListFacets(
	jobs: readonly Job[],
	errorsOf: (job: Job) => readonly ErrorNote[],
): { errorTypes: JobListFacet[]; companies: JobListFacet[]; statuses: JobListFacet[] } {
	const tally = (values: (job: Job) => string[]) => tallyJobs(jobs, values);
	return {
		errorTypes: tally((job) => jobListErrorTypesOf(errorsOf(job))),
		companies: tally((job) => [jobListCompanyOf(job)]),
		statuses: tally((job) => [jobListStatusOf(job)]),
	};
}

/** How many of `jobs` have each value `values` finds on them, most common first. A job counts once per value. */
export function tallyJobs(jobs: readonly Job[], values: (job: Job) => readonly string[]): JobListFacet[] {
	const counts = new Map<string, number>();
	for (const job of jobs) for (const value of new Set(values(job))) counts.set(value, (counts.get(value) ?? 0) + 1);
	return Array.from(counts, ([value, count]) => ({ value, count })).sort(
		(a, b) => b.count - a.count || a.value.localeCompare(b.value),
	);
}
